import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Link,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { useCallback, useMemo, useState } from "react";
import { WasteStreamValidationResult } from "generated-graphql/graphql";

interface WasteStreamData {
  id: string;
  name: string;
}

interface WasteStreamFlatFileExportModalProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (excludeInvalid: boolean) => void;
  validationResults: WasteStreamValidationResult[];
  wasteStreams: WasteStreamData[];
  totalWasteStreamCount: number;
  onWasteStreamClick?: (wasteStreamId: string) => void;
}

type WasteStreamWithIssues = {
  wasteStreamId: string;
  wasteStreamName: string;
  issues: WasteStreamValidationResult["issues"];
};

export function WasteStreamFlatFileExportModal({
  open,
  onClose,
  onGenerate,
  validationResults,
  wasteStreams,
  totalWasteStreamCount,
  onWasteStreamClick,
}: WasteStreamFlatFileExportModalProps) {
  const theme = useTheme();
  const [includeInvalid, setIncludeInvalid] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const wasteStreamsWithIssues: WasteStreamWithIssues[] = useMemo(() => {
    return validationResults
      .filter((result) => result.issues.length > 0)
      .map((result) => {
        const wasteStream = wasteStreams.find(
          (ws) => ws.id === result.wasteStreamId
        );
        return {
          wasteStreamId: result.wasteStreamId,
          wasteStreamName: wasteStream?.name ?? "",
          issues: result.issues,
        };
      });
  }, [validationResults, wasteStreams]);

  const validWasteStreamCount =
    totalWasteStreamCount - wasteStreamsWithIssues.length;

  const toggleRowExpanded = useCallback((wasteStreamId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(wasteStreamId)) {
        next.delete(wasteStreamId);
      } else {
        next.add(wasteStreamId);
      }
      return next;
    });
  }, []);

  const handleExportCsv = useCallback(() => {
    const csvRows: string[] = [
      ["Waste Stream ID", "Waste Stream Name", "Validation Issue"].join(","),
    ];

    for (const ws of wasteStreamsWithIssues) {
      for (const issue of ws.issues) {
        const escapedName = `"${ws.wasteStreamName.replace(/"/g, '""')}"`;
        const escapedMessage = `"${issue.message.replace(/"/g, '""')}"`;
        csvRows.push([ws.wasteStreamId, escapedName, escapedMessage].join(","));
      }
    }

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `validation-issues-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [wasteStreamsWithIssues]);

  const handleGenerate = useCallback(() => {
    onGenerate(!includeInvalid);
  }, [onGenerate, includeInvalid]);

  const generateButtonText = includeInvalid
    ? `Generate All (${totalWasteStreamCount} waste streams)`
    : `Generate Valid Only (${validWasteStreamCount} waste streams)`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Validation Issues Detected"
      maxWidth="lg"
      fullWidth
      noPrimaryAction={true}
      sx={{ zIndex: (theme) => theme.zIndex.modal - 1 }}
      secondaryActionElements={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
      ]}
      additionalPrimaryActionElements={[
        <Button
          key="generate"
          variant="contained"
          onClick={handleGenerate}
          sx={{
            color: theme.palette.primary.contrastText,
            backgroundColor: includeInvalid
              ? theme.palette.warning.light
              : theme.palette.primary.main,
          }}
        >
          {generateButtonText}
        </Button>,
      ]}
    >
      <Box>
        <Typography sx={{ mb: 3, color: "text.secondary" }}>
          The following waste streams have validation issues that should be
          resolved before generating the RCRAInfo flat file. You can still
          generate the file, but these issues may cause problems during
          submission.
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={includeInvalid}
                onChange={(e) => setIncludeInvalid(e.target.checked)}
              />
            }
            label="Include waste streams with errors"
          />
          <Tooltip title="Export validation issues as CSV">
            <IconButton onClick={handleExportCsv} size="small" color="info">
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            maxHeight: 400,
            overflow: "auto",
          }}
        >
          {wasteStreamsWithIssues.map((ws, index) => {
            const isExpanded = expandedRows.has(ws.wasteStreamId);
            return (
              <Box
                key={ws.wasteStreamId}
                sx={{
                  borderBottom:
                    index < wasteStreamsWithIssues.length - 1
                      ? `1px solid ${theme.palette.divider}`
                      : "none",
                }}
              >
                <Box
                  onClick={() => toggleRowExpanded(ws.wasteStreamId)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1.5,
                    gap: 1,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box sx={{ p: 0.5, display: "flex", alignItems: "center" }}>
                    {isExpanded ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </Box>
                  <Link
                    component="button"
                    variant="subtitle2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onWasteStreamClick?.(ws.wasteStreamId);
                    }}
                    sx={{
                      fontWeight: 600,
                      textAlign: "left",
                    }}
                  >
                    {ws.wasteStreamName}
                  </Link>
                  <Chip
                    size="small"
                    label={`${ws.issues.length} issue${
                      ws.issues.length === 1 ? "" : "s"
                    }`}
                    color="warning"
                    variant="outlined"
                  />
                </Box>
                <Collapse in={isExpanded}>
                  <Box sx={{ pl: 6, pr: 2, pb: 2 }}>
                    {ws.issues.map((issue, issueIndex) => (
                      <Box
                        key={issueIndex}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <WarningIcon
                          color="warning"
                          sx={{ fontSize: 16, mt: 0.25, flexShrink: 0 }}
                        />
                        <Typography variant="body2">{issue.message}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Dialog>
  );
}
